import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  ClipboardList,
  History,
  CalendarClock,
  PackageSearch,
  Boxes,
  Truck,
  BarChart3,
  Bell,
  Settings,
  HelpCircle,
  LogOut,
  ShieldCheck,
  X,
} from "lucide-react";
import type { Permission } from "@sbd-tcic/shared-types";
import { useAuth } from "../lib/auth";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission?: Permission;
}
interface NavGroup {
  label: string | null;
  items: NavItem[];
}

/**
 * DS-CMP-005 — one shared, role-filtered navigation config. Every item routes to a distinct
 * URL, so React Router's own NavLink active-matching naturally prevents the "multiple items
 * highlighted at once" defect found and fixed in the reference prototype this session — there
 * is no shared "key" for two different-looking nav rows to collide on here.
 */
const NAV: NavGroup[] = [
  { label: null, items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, permission: "dashboard.read" }] },
  {
    label: "Orders",
    items: [
      { to: "/pos", label: "POS", icon: ShoppingCart, permission: "pos.create_order" },
      { to: "/repair-orders/new", label: "Repair Order", icon: ClipboardList, permission: "repair_orders.create" },
      { to: "/orders", label: "Order History & Notifications", icon: History, permission: "order_history.read" },
    ],
  },
  {
    label: "Inventory",
    items: [
      { to: "/receiving", label: "Receiving", icon: PackageSearch, permission: "receiving.receive" },
      { to: "/inventory", label: "Parts Inventory", icon: Boxes, permission: "inventory.read" },
      { to: "/shipping", label: "Shipping", icon: Truck, permission: "shipping.manage" },
    ],
  },
  {
    label: "Programs & Reports",
    items: [
      { to: "/service-programs", label: "Service Programs", icon: CalendarClock, permission: "service_programs.read" },
      { to: "/oversight", label: "Approvals & Oversight", icon: ShieldCheck, permission: "management_oversight.approve_pricing" },
      { to: "/reports", label: "Reports", icon: BarChart3, permission: "reports.read" },
      { to: "/alerts", label: "Alerts", icon: Bell, permission: "dashboard.read" },
    ],
  },
];

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, can, logout } = useAuth();

  return (
    <>
      {/* Mobile-only backdrop — tapping it closes the drawer. Desktop (lg:) never renders this
       *  branch's visual effect since the sidebar itself is always open there. */}
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={onClose} aria-hidden="true" />}

      <aside
        className={`fixed lg:sticky left-0 top-0 h-screen w-64 flex-shrink-0 z-40 lg:z-auto flex flex-col bg-gradient-to-b from-brand-navy to-brand-navy-deep text-slate-300 overflow-hidden transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2.5 px-5 py-5 border-b border-white/10 flex-shrink-0">
          <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
            <Settings size={18} className="text-white" />
          </div>
          <div className="font-extrabold text-sm tracking-wide text-white uppercase flex-1">Service Center</div>
          <button onClick={onClose} className="lg:hidden w-7 h-7 rounded-md flex items-center justify-center text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Close menu">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 py-3.5 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,.18)_transparent]">
          {NAV.map((group, gi) => {
            const visibleItems = group.items.filter((item) => !item.permission || can(item.permission));
            if (visibleItems.length === 0) return null;
            return (
              <div key={gi}>
                {group.label && <div className="text-[10.5px] font-bold tracking-wider text-slate-500 uppercase px-2.5 pt-3.5 pb-1.5">{group.label}</div>}
                {visibleItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13.5px] font-medium mb-0.5 transition-colors ${
                        isActive ? "bg-brand-blue text-white shadow-[0_4px_10px_rgba(47,111,237,.35)]" : "text-slate-300 hover:bg-white/[.07] hover:text-white"
                      }`
                    }
                  >
                    <item.icon size={17} strokeWidth={2} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-white/10 flex-shrink-0">
          <button className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13.5px] font-medium text-slate-300 hover:bg-white/[.07] hover:text-white w-full text-left">
            <HelpCircle size={17} />
            <span>Help</span>
          </button>
          <button onClick={() => void logout()} className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13.5px] font-medium text-slate-300 hover:bg-white/[.07] hover:text-white w-full text-left">
            <LogOut size={17} />
            <span>Logout{user ? ` (${user.name})` : ""}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
